/**
 * 数值 / 日期展示统一入口
 * ------------------------------------------------------------------
 * 为什么需要它：看板与档案页到处是金额、人数、比率、时长、日期，
 * 此前各页各自 toFixed / 拼字符串，同一含义出现多种写法（1,234 / 1234 / 1234.00），
 * 也让"数字等宽对齐"没法统一实施。
 *
 * 约定：
 *   1. 一切数值展示走本文件，业务组件不再自己 toFixed / 拼单位；
 *   2. 空值统一 `EMPTY`（`--`），**不要用 0 糊**（0 与"无数据"是两件事）；
 *   3. 返回的都是字符串；单位由调用方用小字单独渲染（便于做浅色小字后缀）。
 */
import dayjs from 'dayjs';

/** 空值占位符：与 BFF 的 null 语义约定配套（见 M1 §12.2） */
export const EMPTY = '--';

/** 是否为空（null / undefined / 空串 / NaN） */
export const isBlank = (v) =>
  v === null ||
  v === undefined ||
  v === '' ||
  (typeof v === 'number' && Number.isNaN(v));

/**
 * 千分位数字
 * @param value     数值
 * @param precision 小数位，默认 0
 */
export const formatNumber = (value, precision = 0) => {
  if (isBlank(value)) return EMPTY;
  const num = Number(value);
  if (Number.isNaN(num)) return EMPTY;
  return num.toLocaleString('zh-CN', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

/**
 * 金额（千分位 + 固定小数位，默认 2 位）
 * @param value     数值
 * @param precision 小数位，默认 2
 */
export const formatMoney = (value, precision = 2) =>
  formatNumber(value, precision);

/**
 * 百分比
 * @param value   数值；默认按「已经是百分数」处理（12.5 → 12.5%）
 * @param ratio   true 时按小数处理（0.125 → 12.5%）
 * @param precision 小数位，默认 1
 */
export const formatPercent = (value, { ratio = false, precision = 1 } = {}) => {
  if (isBlank(value)) return EMPTY;
  const num = Number(value);
  if (Number.isNaN(num)) return EMPTY;
  const percent = ratio ? num * 100 : num;
  return `${formatNumber(percent, precision)}%`;
};

/**
 * 时长（分钟 → 人类可读）
 * @param minutes 分钟数
 */
export const formatDuration = (minutes) => {
  if (isBlank(minutes)) return EMPTY;
  const m = Number(minutes);
  if (Number.isNaN(m)) return EMPTY;
  if (m < 60) return `${formatNumber(m)} 分钟`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} 小时 ${rest} 分` : `${h} 小时`;
};

/** 日期 YYYY-MM-DD */
export const formatDate = (value) => {
  if (isBlank(value)) return EMPTY;
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : EMPTY;
};

/** 日期时间 YYYY-MM-DD HH:mm */
export const formatDateTime = (value) => {
  if (isBlank(value)) return EMPTY;
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : EMPTY;
};

/**
 * 环比：把 BFF 给的方向 + 比率转成统一的展示结构
 * 返回 { text, direction }，direction 用于选色；两者都要用（不能只靠颜色表达涨跌）
 */
export const formatTrend = (rate, direction) => {
  if (isBlank(rate)) return null;
  const num = Number(rate);
  /* rate 可能是数字，也可能是字符串；非数值时宁可不出结论，不要猜方向 */
  if (!Number.isFinite(num)) return null;
  const dir = direction || (num >= 0 ? 'up' : 'down');
  return {
    text: `${dir === 'down' ? '▼' : '▲'} ${formatPercent(Math.abs(num))}`,
    direction: dir,
  };
};
